'use client'

import React, { useEffect } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Download, Keyboard, Mic, TrashIcon, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { BasePhoto } from '@/features/base/types/base-content'
import PhotoDescriptionIndicator from '@/features/photos/components/photo-description-indicator'
import { ZoomableImage } from '@/features/photos/components/zoomable-image'
import { downloadPhotoOrVideo } from '@/features/photos/lib/download-media'

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Unknown date'
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export interface BasePhotoViewerProps {
  photo: BasePhoto
  isOpen: boolean
  onClose: () => void
  description?: string | null
  pendingDescription?: string | null
  isTranscribing?: boolean
  canEdit?: boolean
  /** When true, show Download button (Admin/Base Leader only). */
  canDownload?: boolean
  onDelete?: (photoId: number) => void | Promise<void>
  onVoiceClick?: () => void
  onKeyboardClick?: () => void
  /** Optional: list of all photos for carousel; currentIndex + onSelectIndex must be provided too. */
  allPhotos?: BasePhoto[]
  /** Index of `photo` within `allPhotos` (0-based). */
  currentIndex?: number
  /** Called when user selects previous/next photo; receives new index. */
  onSelectIndex?: (index: number) => void
  /** When true, show Confidential tag as active (for toggle button). */
  hasConfidentialTag?: boolean
  /** When provided and canEdit, show Confidential toggle button. */
  onToggleConfidential?: () => void | Promise<void>
}

/**
 * Full-screen photo/document viewer - same UI as the Base Photo timeline tab.
 * Used by both the timeline and the Photos sections for consistent UX.
 */
export default function BasePhotoViewer({
  photo,
  isOpen,
  onClose,
  description,
  pendingDescription,
  isTranscribing = false,
  canEdit = false,
  canDownload = false,
  onDelete,
  onVoiceClick,
  onKeyboardClick,
  allPhotos,
  currentIndex = 0,
  onSelectIndex,
  hasConfidentialTag = false,
  onToggleConfidential,
}: BasePhotoViewerProps) {
  const canNavigate =
    allPhotos &&
    allPhotos.length > 1 &&
    typeof currentIndex === 'number' &&
    typeof onSelectIndex === 'function'
  const hasPrev = canNavigate && currentIndex > 0
  const hasNext = canNavigate && currentIndex < allPhotos.length - 1

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (canNavigate && e.key === 'ArrowLeft' && hasPrev) {
        e.preventDefault()
        onSelectIndex(currentIndex - 1)
      }
      if (canNavigate && e.key === 'ArrowRight' && hasNext) {
        e.preventDefault()
        onSelectIndex(currentIndex + 1)
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', onKeyDown)
      return () => document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, onClose, canNavigate, hasPrev, hasNext, currentIndex, onSelectIndex])

  if (!isOpen) return null

  const displayDesc = pendingDescription ?? description
  const isDoc = (photo.mediaType ?? 'image') === 'document'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
      <div className="relative w-[700px] max-w-[calc(100vw-2rem)] h-[80vh] mx-4">
        <div className="relative w-full h-full bg-white rounded-lg overflow-hidden flex flex-col">
          <div className="flex-1 relative min-h-0">
            {/* Carousel arrows (only for images/documents when multiple photos) */}
            {canNavigate && (
              <>
                {hasPrev && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white border-0"
                    onClick={() => onSelectIndex(currentIndex - 1)}
                    aria-label="Previous photo"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                )}
                {hasNext && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white border-0"
                    onClick={() => onSelectIndex(currentIndex + 1)}
                    aria-label="Next photo"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                )}
              </>
            )}
            {isDoc ? (
              <iframe
                src={photo.imageUrl}
                title={description || `Doc ${photo.id}`}
                className="w-full h-full rounded"
              />
            ) : (
              <ZoomableImage
                src={photo.imageUrl}
                alt={description || `Photo ${photo.id}`}
                className="w-full h-full"
                showControls
              />
            )}
            <div className="absolute bottom-2 right-2">
              <PhotoDescriptionIndicator
                hasDescription={(displayDesc?.trim().length ?? 0) > 0}
              />
            </div>
          </div>
          <div className="bg-white p-3 border-t flex-shrink-0">
            {isTranscribing ? (
              <p className="text-sm text-muted-foreground">Transcribing...</p>
            ) : (
              displayDesc && (
                <p className="text-sm mb-3 pb-3 border-b whitespace-pre-wrap">
                  {displayDesc}
                </p>
              )
            )}
            {canEdit && (
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex gap-2">
                  {onVoiceClick && (
                    <Button variant="outline" size="sm" onClick={onVoiceClick}>
                      <Mic className="w-4 h-4" /> Voice
                    </Button>
                  )}
                  {onKeyboardClick && (
                    <Button variant="outline" size="sm" onClick={onKeyboardClick}>
                      <Keyboard className="w-4 h-4" /> Keyboard
                    </Button>
                  )}
                </div>
                {onToggleConfidential && (
                  <Button
                    type="button"
                    variant={hasConfidentialTag ? 'default' : 'outline'}
                    size="sm"
                    className="shrink-0"
                    onClick={() => onToggleConfidential()}
                    aria-label={
                      hasConfidentialTag ? 'Remove Confidential tag' : 'Add Confidential tag'
                    }
                  >
                    Confidential {hasConfidentialTag ? 'On' : 'Off'}
                  </Button>
                )}
              </div>
            )}
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">
                {photo.uploadedByUser?.fullname && (
                  <span className="block mb-0.5">
                    Added by {photo.uploadedByUser.fullname}
                  </span>
                )}
                {formatDate((photo.takenAt || photo.uploadedAt) as string)}
              </p>
              <div className="flex items-center gap-3">
                {canDownload && (
                  <Button
                    variant="default"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() =>
                      downloadPhotoOrVideo(photo.imageUrl, {
                        id: photo.id,
                        mediaType: (photo.mediaType as 'image' | 'video' | 'document') ?? 'image',
                        downloadApiPath: `/api/base-photos/${photo.id}/download`,
                      })
                    }
                    aria-label="Download"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                )}
                {canEdit && onDelete && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(photo.id)
                    }}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
